import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IconButton } from "@fluentui/react/lib/Button";
import { Stack } from "@fluentui/react";
import { useCloseIconStyle } from "../../theme/styles/CloseIconStyle";
import { useCheckinPanelStyle } from "../../theme/styles/CheckinPanelStyle";
import { usePreviewInTeams } from "../../redux/hooks/usePreviewInTeams";
import { useTheme } from "../../theme/OAS365Theme";
import {
  PREVIEW_URL_EMPTY,
  PREVIEW_URL_ERROR,
} from "../../redux/constants/messageTypes";
import { APP_ROUTES, NAV_LOCATION_STATE } from "../../routes/routeConfig";

const cancelIcon = { iconName: "Cancel" };

export default function PreviewPage() {
  const { state: previewState } = usePreviewInTeams();
  const previewPayload = previewState?.previewUrl ?? "";

  const previewGetUrl = useMemo(() => {
    if (typeof previewPayload === "string") return previewPayload;
    if (previewPayload && typeof previewPayload === "object") return previewPayload?.getUrl ?? "";
    return "";
  }, [previewPayload]);

  const previewPostUrl = useMemo(() => {
    if (previewPayload && typeof previewPayload === "object") return previewPayload?.postUrl ?? "";
    return "";
  }, [previewPayload]);

  const previewWebUrl = useMemo(() => {
    if (previewPayload && typeof previewPayload === "object") return previewPayload?.webUrl ?? "";
    return "";
  }, [previewPayload]);

  const previewPostParameters = useMemo(() => {
    if (previewPayload && typeof previewPayload === "object") return previewPayload?.postParameters ?? "";
    return "";
  }, [previewPayload]);

  const postInputs = useMemo(() => {
    if (!previewPostParameters || typeof previewPostParameters !== "string") return [];
    try {
      const params = new URLSearchParams(previewPostParameters);
      return Array.from(params.entries());
    } catch (e) {
      return [];
    }
  }, [previewPostParameters]);

  const shouldUsePost = !!previewPostUrl;

  const formRef = useRef(null);
  const [hasSubmittedPost, setHasSubmittedPost] = useState(false);

  useEffect(() => {
    if (!shouldUsePost) return;
    if (hasSubmittedPost) return;
    setHasSubmittedPost(true);
    formRef.current?.submit?.();
  }, [shouldUsePost, hasSubmittedPost]);

  useEffect(() => {
    // Reset between different preview requests.
    setHasSubmittedPost(false);
  }, [previewPostUrl, previewPostParameters]);
  const theme = useTheme();
  const styleTheme = useCloseIconStyle();
  const navigate = useNavigate();
  const location = useLocation();
  const panelStyle = useCheckinPanelStyle();

  const returnTo = location.state?.returnTo ?? APP_ROUTES.allDocuments;
  const returnState = location.state?.returnState ?? {};

  const closeAndNavigate = () => {
    navigate(returnTo, {
      state: {
        ...returnState,
        [NAV_LOCATION_STATE.skipDocumentPanelRouteRefresh]: true,
      },
    });
  };

  return (
    <Stack
      enableScopedSelectors
      styles={panelStyle.panelStyle}
      style={{ height: "calc(100vh - 50px)", overflow: "hidden" }}
      tokens={{ childrenGap: 0 }}
    >
      <Stack.Item style={{ display: "flex", justifyContent: "right", flexShrink: 0 }}>
        <IconButton
          styles={styleTheme.CloseIconStyle}
          iconProps={cancelIcon}
          ariaLabel="Close popup modal"
          onClick={() => closeAndNavigate()}
          title="Close"
        />
      </Stack.Item>
      {!previewWebUrl && !previewGetUrl && !shouldUsePost && (
        <Stack.Item style={{ display: "flex", justifyContent: "center" }}>
          <h4
            style={{
              color: theme.colors.fontColor,
              textAlign: "center",
              fontFamily: theme.fonts.regular,
            }}
          >
            {PREVIEW_URL_EMPTY}
          </h4>
        </Stack.Item>
      )}
      {previewState?.error && (
        <Stack.Item style={{ display: "flex", justifyContent: "center" }}>
          <h4
            style={{
              color: "black",
              textAlign: "center",
              fontFamily: theme.fonts.regular,
            }}
          >
            {PREVIEW_URL_ERROR}
          </h4>
        </Stack.Item>
      )}
      {shouldUsePost && !previewState?.error && (
        <>
          <Stack.Item
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
            }}
          >
            <iframe
              name="previewPostFrame"
              style={{
                width: "100%",
                height: "100%",
                minHeight: "calc(100vh - 120px)",
              }}
              frameBorder="0"
              title="Document preview"
            />
          </Stack.Item>
          <form
            ref={formRef}
            action={previewPostUrl}
            method="POST"
            target="previewPostFrame"
            style={{ display: "none" }}
          >
            {postInputs.map(([k, v]) => (
              <input key={k} type="hidden" name={k} value={v} />
            ))}
          </form>
        </>
      )}
      {/* Prefer the embedded viewer URL (`embed.aspx`) over `Doc.aspx` (`webUrl`).
          `Doc.aspx` is often blocked from being framed by SharePoint CSP (`frame-ancestors`). */}
      {!shouldUsePost && previewGetUrl && !previewState?.error && (
        <Stack.Item
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
          }}
        >
          <iframe
            src={previewGetUrl}
            style={{
              width: "100%",
              height: "100%",
              minHeight: "calc(100vh - 120px)",
            }}
            frameBorder="0"
            title="Document preview"
          />
        </Stack.Item>
      )}
      {!shouldUsePost && !previewGetUrl && previewWebUrl && !previewState?.error && (
        <Stack.Item
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
          }}
        >
          <iframe
            src={previewWebUrl}
            style={{
              width: "100%",
              height: "100%",
              minHeight: "calc(100vh - 120px)",
            }}
            frameBorder="0"
            title="Document preview"
          />
        </Stack.Item>
      )}
    </Stack>
  );
}
